import {
  type ManuallyLaunchableWorkflow,
  useCreateClientProcessWorkflowMutation,
} from '@cartwheel/api/v1'
import { InfoBanner, Modal, useToast } from '@cartwheel/components'
import { useQueryClient } from '@tanstack/react-query'

interface Props {
  isOpen: boolean
  onClose: () => void
  clientId: string
  manuallyLaunchableWorkflows: ManuallyLaunchableWorkflow[]
  onLaunched: () => void
}

export function StartNewWorkflowModal({
  isOpen,
  onClose,
  clientId,
  manuallyLaunchableWorkflows,
  onLaunched,
}: Props) {
  const toast = useToast()
  const queryClient = useQueryClient()

  const mutation = useCreateClientProcessWorkflowMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [{ _id: 'clientProcessWorkflows' }] })
      toast.info({
        description: 'Starting workflow. It may take a moment to appear in the list.',
      })
      onLaunched()
    },
    onError: () => {
      toast.error({ description: 'Could not start the workflow. Please try again.' })
    },
  })

  const title = (
    <div className="flex flex-col gap-1">
      <span>Start new workflow</span>
      <span className="font-sans text-base font-normal text-charcoal-75">
        Select from the available workflows below to begin
      </span>
    </div>
  )

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <div className="flex flex-col gap-3 px-6 pb-6">
        {manuallyLaunchableWorkflows.map((workflow) => (
          <button
            key={workflow.type}
            type="button"
            disabled={mutation.isPending}
            onClick={() =>
              mutation.mutate({ path: { client_id: clientId }, body: { type: workflow.type } })
            }
            className="flex items-center gap-4 rounded-lg border border-gray-stroke bg-white p-4 text-left hover:bg-sand-25 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-sky-25">
              <i className={`fa-solid fa-${workflow.icon} text-sea`} aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-base font-semibold text-charcoal">{workflow.title}</div>
              <div className="text-sm text-charcoal-50">{workflow.description}</div>
            </div>
          </button>
        ))}
        <InfoBanner
          tone="sky"
          title="More workflow types coming soon!"
          description="We'll be adding new workflow options here to help streamline more care coordination processes"
        />
      </div>
    </Modal>
  )
}
